import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  KanbanSquare,
  LayoutDashboard,
  Phone,
  Receipt,
  Settings,
  UserCog,
  Users,
} from 'lucide-react';
import type { BusinessVertical } from '@/lib/vertical';
import { isRestaurantVertical } from '@/lib/vertical';

export interface CrmNavItem {
  path: string;
  label: string;
}

/** Top-level CRM areas — each maps to 1–3 screens via sub-nav. */
export interface CrmNavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Default route when clicking the primary tab. */
  defaultPath: string;
  /** First URL segment(s) after /crm/:businessId/ that belong to this group. */
  matchPaths: string[];
  items: CrmNavItem[];
}

export const CRM_NAV_GROUPS: CrmNavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    defaultPath: 'overview',
    matchPaths: ['overview'],
    items: [],
  },
  {
    id: 'jobs',
    label: 'Jobs',
    icon: KanbanSquare,
    defaultPath: 'pipeline',
    matchPaths: ['pipeline', 'jobs'],
    items: [],
  },
  {
    id: 'contacts',
    label: 'Contacts',
    icon: Users,
    defaultPath: 'customers',
    matchPaths: ['customers', 'inbox'],
    items: [
      { path: 'customers', label: 'Directory' },
      { path: 'inbox', label: 'Inbox' },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    icon: BarChart3,
    defaultPath: 'analytics',
    matchPaths: ['analytics', 'grid', 'documents', 'insights'],
    items: [
      { path: 'analytics', label: 'Analytics' },
      { path: 'grid', label: 'Grid' },
      { path: 'documents', label: 'Documents' },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    icon: UserCog,
    defaultPath: 'team',
    matchPaths: ['team', 'reports', 'payroll'],
    items: [
      { path: 'reports', label: 'Reports' },
      { path: 'team', label: 'Technicians' },
      { path: 'payroll', label: 'Payroll' },
    ],
  },
  {
    id: 'system',
    label: 'Settings',
    icon: Settings,
    defaultPath: 'settings',
    matchPaths: ['settings', 'workflows', 'activity'],
    items: [
      { path: 'settings', label: 'Settings' },
      { path: 'workflows', label: 'Workflows' },
      { path: 'activity', label: 'Activity' },
    ],
  },
];

export const RESTAURANT_NAV_GROUPS: CrmNavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard,
    defaultPath: 'overview',
    matchPaths: ['overview'],
    items: [],
  },
  {
    id: 'calls',
    label: 'Calls',
    icon: Phone,
    defaultPath: 'activity',
    matchPaths: ['activity'],
    items: [],
  },
  {
    id: 'orders',
    label: 'Orders',
    icon: Receipt,
    defaultPath: 'orders',
    matchPaths: ['orders'],
    items: [],
  },
  {
    id: 'system',
    label: 'Settings',
    icon: Settings,
    defaultPath: 'settings',
    matchPaths: ['settings'],
    items: [{ path: 'settings', label: 'Settings' }],
  },
];

export const TRADES_ONLY_SEGMENTS = new Set([
  'pipeline', 'jobs', 'customers', 'inbox', 'analytics', 'grid',
  'documents', 'workflows', 'reports', 'team', 'payroll',
]);

export function getNavGroups(vertical?: string | null): CrmNavGroup[] {
  return isRestaurantVertical(vertical) ? RESTAURANT_NAV_GROUPS : CRM_NAV_GROUPS;
}

/** Icons for ⌘K palette subtitles (optional). */
export const CRM_ROUTE_META: Record<string, { label: string; group: string }> = {
  overview:  { label: 'Overview',  group: 'Home' },
  pipeline:  { label: 'Pipeline',  group: 'Jobs' },
  customers: { label: 'Directory', group: 'Contacts' },
  inbox:     { label: 'Inbox',     group: 'Contacts' },
  analytics: { label: 'Analytics', group: 'Insights' },
  grid:      { label: 'Grid',      group: 'Insights' },
  documents: { label: 'Documents', group: 'Insights' },
  reports:   { label: 'Reports',     group: 'Team' },
  team:      { label: 'Technicians', group: 'Team' },
  payroll:   { label: 'Payroll',     group: 'Team' },
  settings:  { label: 'Settings',  group: 'Settings' },
  workflows: { label: 'Workflows', group: 'Settings' },
  activity:  { label: 'Activity',  group: 'Settings' },
  orders:    { label: 'Orders',    group: 'Orders' },
};

export function crmBase(businessId: string) {
  return `/crm/${businessId}`;
}

export function crmPath(businessId: string, segment: string) {
  return `${crmBase(businessId)}/${segment}`;
}

/** Resolve which nav group is active from the current pathname. */
export function resolveCrmNav(pathname: string, businessId: string, vertical?: string | null) {
  const prefix = `${crmBase(businessId)}/`;
  const tail = pathname.startsWith(prefix) ? pathname.slice(prefix.length) : '';
  const section = tail.split('/')[0] || 'overview';
  const groups = getNavGroups(vertical);

  const group =
    groups.find(g => g.matchPaths.includes(section)) ??
    groups[0];

  return { group, section };
}

/** Whether the workspace should show the full hero (only on Overview). */
export function showCrmHero(pathname: string, businessId: string, vertical?: string | null) {
  const { section } = resolveCrmNav(pathname, businessId, vertical);
  return section === 'overview';
}

/** CRM sections for mobile hamburger — primary tab + sub-pages (Pipeline, Inbox, …). */
export function getCrmMenuSections(businessId: string, vertical?: string | null) {
  return getNavGroups(vertical).map(g => ({
    id: g.id,
    label: g.label,
    icon: g.icon,
    primaryTo: crmPath(businessId, g.defaultPath),
    items: g.items.map(i => ({
      to: crmPath(businessId, i.path),
      label: i.label,
    })),
  }));
}

export function parseCrmBusinessId(pathname: string): string | null {
  const m = pathname.match(/^\/crm\/([^/]+)/);
  return m?.[1] ?? null;
}

/** Business id from CRM or dashboard URLs (for global mobile nav). */
export function parseActiveBusinessId(pathname: string): string | null {
  return parseCrmBusinessId(pathname) ?? pathname.match(/^\/dashboard\/([^/]+)/)?.[1] ?? null;
}

export const CRM_OPEN_PALETTE_EVENT = 'crm:open-palette';

export function openCrmPalette() {
  window.dispatchEvent(new CustomEvent(CRM_OPEN_PALETTE_EVENT));
}

export type { BusinessVertical };
